import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import {
  fetchMessagingContacts,
  fetchPhotoImage,
  fetchPhotos,
  removeMyPhoto,
  uploadMyPhoto,
  type CandidatePhotos,
} from '../api/candidate';
import { squarePhoto } from '../lib/squarePhoto';
import { useAuthStore } from '../store/auth';

// Photos de profil dans l'espace élève : la sienne et celles des moniteurs de son école. Les leçons
// et les bilans portent déjà l'adresse de la photo de leur moniteur ; la messagerie la trouve par
// le compte du moniteur. Sans photo, pendant le chargement ou en cas d'échec : les initiales.

const PHOTOS_KEY = ['candidate', 'photos'] as const;
const IMAGE_KEY = 'candidate-photo-image';
const NONE: CandidatePhotos = { me: null, staff: {} };

export function useMyPhotos(): CandidatePhotos {
  const candidateId = useAuthStore((s) => s.candidate?.id);
  const { data } = useQuery({
    queryKey: [...PHOTOS_KEY, candidateId],
    queryFn: fetchPhotos,
    enabled: !!candidateId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data ?? NONE;
}

async function loadImage(url: string): Promise<string> {
  const local = URL.createObjectURL(await fetchPhotoImage(url));
  try {
    const probe = new Image();
    probe.src = local;
    await probe.decode();
    return local;
  } catch (error) {
    URL.revokeObjectURL(local);
    throw error;
  }
}

/** Image prête à afficher d'une photo, ou `undefined` (initiales). */
export function usePhoto(url?: string | null): string | undefined {
  const client = useQueryClient();
  useEffect(
    () =>
      client.getQueryCache().subscribe((event) => {
        const local = event.query.state.data;
        if (event.type === 'removed' && event.query.queryKey[0] === IMAGE_KEY && typeof local === 'string')
          URL.revokeObjectURL(local);
      }),
    [client],
  );
  return useQuery({
    queryKey: [IMAGE_KEY, url ?? ''],
    queryFn: () => loadImage(url as string),
    enabled: !!url,
    staleTime: Infinity,
    retry: false,
  }).data;
}

/** Photo posée sur des initiales (la pastille qui la porte coupe ce qui dépasse) ; rien sans photo. */
export function Photo({ url, className = 'photo-cover' }: { url?: string | null; className?: string }) {
  const src = usePhoto(url);
  return src ? <img src={src} alt="" className={className} /> : null;
}

/** Photo d'un moniteur de la messagerie, retrouvé par son nom parmi les contacts de l'élève. */
export function useInstructorPhotoByName(): (name?: string | null) => string | null {
  const { staff } = useMyPhotos();
  const contacts = useQuery({
    queryKey: ['candidate', 'messaging', 'contacts'],
    queryFn: fetchMessagingContacts,
    staleTime: 60_000,
  });
  const instructors = Array.isArray(contacts.data?.instructors) ? contacts.data.instructors : [];
  return (name) => {
    if (!name) return null;
    const matches = instructors.filter((instructor) => instructor.name === name);
    return matches.length === 1 ? (staff[matches[0].id] ?? null) : null;
  };
}

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX_BYTES = 8 * 1024 * 1024;

const failure = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/** Sa photo de profil : l'élève l'ajoute, la change ou la retire. */
export function MyPhotoEditor({ initials }: { initials: string }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { me } = useMyPhotos();
  const refresh = () => queryClient.invalidateQueries({ queryKey: PHOTOS_KEY });
  const send = useMutation({
    mutationFn: async (file: File) => uploadMyPhoto(await squarePhoto(file)),
    onSuccess: refresh,
    onError: (e) => setError(failure(e, 'Envoi de la photo impossible.')),
  });
  const remove = useMutation({
    mutationFn: removeMyPhoto,
    onSuccess: refresh,
    onError: (e) => setError(failure(e, 'Retrait de la photo impossible.')),
  });
  const busy = send.isPending || remove.isPending;
  const choose = (file?: File) => {
    if (!file || busy) return;
    setError(null);
    if (!PHOTO_TYPES.includes(file.type)) return setError('Format non pris en charge : JPEG, PNG ou WebP attendu.');
    if (file.size > PHOTO_MAX_BYTES) return setError('Photo trop volumineuse (8 Mo au plus).');
    send.mutate(file);
  };
  return (
    <div className="flex items-center gap-4 mb-4">
      <span className="relative grid h-16 w-16 flex-shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-lg font-semibold text-white">
        {initials}
        <Photo url={me} />
        {busy && (
          <span className="absolute inset-0 grid place-items-center bg-white/60">
            <Loader2 size={20} className="animate-spin text-indigo-600" />
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <input
          ref={input}
          type="file"
          hidden
          accept={PHOTO_TYPES.join(',')}
          aria-label="Fichier de la photo"
          disabled={busy}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            e.currentTarget.value = '';
            choose(file);
          }}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            <Camera size={14} />
            {me ? 'Changer la photo' : 'Ajouter une photo'}
          </button>
          {me && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                remove.mutate();
              }}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 size={14} />
              Retirer
            </button>
          )}
        </div>
        <p className="mt-1.5 text-xs text-slate-500">Visible par votre auto-école et vos moniteurs.</p>
        {error && (
          <p role="alert" className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
